import { jwtDecode } from "jwt-decode";

const ACCOUNT_SCOPED_PREFIXES = [
  "/admin",
  "/login",
  "/logout",
  "/forgot-password",
  "/reset-password",
  "/register",
  "/verify-email",
  "/actors/onboarding",
  // Google SSO callback and any future /auth/* pages are not account-scoped.
  "/auth",
];
const NON_ACCOUNT_SEGMENTS = new Set([
  "auth",
  "dashboard",
  "campaigns",
  "templates",
  "designer-agent",
  "view-campaigns",
  "edit-campaigns",
  "campaign-table",
  "actor-form",
  "add-actors",
  "actors",
  "contacts",
  "assets",
  "branding",
  "account-settings",
  "super-admin-settings",
  "tenant-settings",
  "users",
  "video-analytics",
  "lms-engagement",
  "metering",
  "manage-tags",
  "crm",
  "video-pipeline",
  "docs",
  "api-docs",
  "actor-campaign",
  "actor-details",
  "editor-interface",
  "edit-draft-campaigns",
  "view-tag",
  "workflows",
  // TVD-1301/1291/1303/1135/1293: SaaS self-serve scoped route first segments.
  "billing",
  "plans",
  "paywalls",
  "choose-path",
  "starter",
  "start",
  "enterprise",
  "pricing",
]);

export const ACTOR_INVITE_ACCOUNT_ID_KEY = "tv_actor_invite_account_id";

export const ACTOR_GLOBAL_PATH_PREFIXES = [
  "/actors/onboarding",
  "/actor-form",
  "/campaign-table",
  "/generate-video",
];

const normalizePath = (value = "/") => (value.startsWith("/") ? value : `/${value}`);

export const getStoredAccountId = () => localStorage.getItem("accountId");

export const isGlobalPath = (pathname = "/") => {
  const normalized = normalizePath(pathname);
  return ACCOUNT_SCOPED_PREFIXES.some((prefix) => normalized.startsWith(prefix));
};

export const getAccountIdFromPathname = (pathname = "/") => {
  const normalized = normalizePath(pathname);
  if (isGlobalPath(normalized)) {
    return null;
  }

  const firstSegment = normalized.split("/").find(Boolean);
  if (NON_ACCOUNT_SEGMENTS.has(firstSegment)) {
    return null;
  }
  return firstSegment || null;
};

export const isActorGlobalPath = (pathname = "/") => {
  const normalized = normalizePath(pathname);
  return ACTOR_GLOBAL_PATH_PREFIXES.some((prefix) => normalized.startsWith(prefix));
};

export const isActorInvitePath = (pathname = "/") => {
  const normalized = normalizePath(pathname);
  return normalized.startsWith("/actors/onboarding/invite");
};

export const getStoredActorInviteAccountId = () => {
  if (typeof sessionStorage === "undefined") {
    return null;
  }
  try {
    return sessionStorage.getItem(ACTOR_INVITE_ACCOUNT_ID_KEY);
  } catch {
    return null;
  }
};

export const setStoredActorInviteAccountId = (accountId) => {
  if (!accountId || typeof sessionStorage === "undefined") {
    return;
  }
  try {
    sessionStorage.setItem(ACTOR_INVITE_ACCOUNT_ID_KEY, String(accountId));
  } catch {
    /* ignore */
  }
};

export const clearStoredActorInviteAccountId = () => {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  try {
    sessionStorage.removeItem(ACTOR_INVITE_ACCOUNT_ID_KEY);
  } catch {
    /* ignore */
  }
};

const getActorAccountIdFromSession = () => {
  try {
    if (typeof localStorage === "undefined" || localStorage.getItem("userType") !== "actor") {
      return null;
    }
    const token = localStorage.getItem("actorToken");
    if (!token) {
      return null;
    }
    const decoded = jwtDecode(token);
    if (decoded?.account_id === null || decoded?.account_id === undefined) {
      return null;
    }
    return String(decoded.account_id);
  } catch {
    return null;
  }
};

/** Actor account id on global actor routes (invite preview session or actor JWT). */
export const resolveActorAccountIdForPath = (pathname = "/") => {
  if (!isActorGlobalPath(pathname)) {
    return null;
  }

  const userType = typeof localStorage !== "undefined" ? localStorage.getItem("userType") : null;
  if (userType === "actor") {
    return getActorAccountIdFromSession() || getStoredActorInviteAccountId();
  }

  if (isActorInvitePath(pathname)) {
    return getStoredActorInviteAccountId();
  }

  return null;
};

export const getActiveAccountId = (pathname = "/", options = {}) => {
  const routeAccountId = getAccountIdFromPathname(pathname);
  if (routeAccountId) {
    return routeAccountId;
  }

  const actorAccountId = resolveActorAccountIdForPath(pathname);
  if (actorAccountId) {
    return actorAccountId;
  }

  const shouldUseStoredFallback = options?.allowStoredFallback === true;
  if (shouldUseStoredFallback) {
    return getStoredAccountId() || null;
  }

  return null;
};

export const stripAccountFromPathname = (pathname = "/") => {
  const normalized = normalizePath(pathname);
  const accountId = getAccountIdFromPathname(normalized);
  if (!accountId) {
    return normalized;
  }

  const stripped = normalized.replace(new RegExp(`^/${accountId}`), "");
  return stripped || "/";
};

export const buildScopedPath = (accountId, pathname = "/dashboard") => {
  const normalized = normalizePath(pathname);
  const stripped = stripAccountFromPathname(normalized);
  const targetPath = stripped === "/" ? "/dashboard" : stripped;

  if (!accountId) {
    return targetPath;
  }

  return `/${accountId}${targetPath}`;
};

export const withScopedQuery = (path, search = "") => {
  return `${path}${search || ""}`;
};

export const replaceAccountInPathname = (targetAccountId, pathname = "/") => {
  const stripped = stripAccountFromPathname(pathname);
  return buildScopedPath(targetAccountId, stripped);
};
